import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { flowFetch, FlowArea, FlowUser, STATUS_LABELS, STATUS_COLORS, formatDate } from './flowApi';
import { TaskDetailModal } from './FlowTasks';

// Visão do administrador: acompanha tarefas-mãe (fluxos multi-área, ex.: criação
// de produto -> fotos no marketing -> postagem no e-commerce) e aponta o gargalo:
// o estágio aberto parado há mais tempo.

interface PipelineStage {
  id: string; title: string; status: string; progress: number;
  dueDate?: string | null; updatedAt: string; ageDays: number; overdue: boolean;
  assignee?: { id: string; nome: string } | null;
  area?: { id: string; name: string; color?: string | null } | null;
  board?: { id: string; name: string } | null;
  checklistTotal: number; checklistDone: number;
  sequenceOrder: number;
  locked: boolean;
  deliveredLate: boolean;
  lateDays: number;
  delayCarryDays: number;
  dueAdjusted?: string | null;
}
interface PipelineItem {
  id: string; title: string; status: string; priority: string; progress: number;
  dueDate?: string | null; updatedAt: string;
  assignee?: { id: string; nome: string } | null;
  createdBy?: { id: string; nome: string } | null;
  area?: { id: string; name: string; color?: string | null } | null;
  board?: { id: string; name: string } | null;
  checklistTotal: number; checklistDone: number;
  sequential: boolean;
  stages: PipelineStage[];
  bottleneckId: string | null;
}

export function FlowPipeline({ areas, users }: { areas: FlowArea[]; users: FlowUser[] }) {
  const [items, setItems] = useState<PipelineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [includeDone, setIncludeDone] = useState(false);
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await flowFetch(`/api/flow/pipeline${includeDone ? '?done=true' : ''}`);
      setItems(data.items || []);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao carregar pipeline');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [includeDone]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-black text-slate-900">Pipeline de fluxos</h2>
          <p className="text-xs text-slate-500">Tarefas com subtarefas em várias áreas. O estágio destacado em vermelho é o gargalo (parado há mais tempo).</p>
        </div>
        <label className="flex items-center gap-2 text-xs font-medium text-slate-600">
          <input type="checkbox" className="accent-cyan-600" checked={includeDone} onChange={e => setIncludeDone(e.target.checked)} />
          Incluir concluídas
        </label>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="h-7 w-7 animate-spin rounded-full border-b-2 border-cyan-600"></div></div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center text-sm text-slate-400">
          Nenhum fluxo em andamento. Crie subtarefas dentro de uma tarefa para acompanhá-la aqui de ponta a ponta.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(item => (
            <div key={item.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <button onClick={() => setOpenTaskId(item.id)} className="min-w-0 text-left">
                  <p className="flex items-center gap-2 truncate text-sm font-bold text-slate-900 hover:text-cyan-700">
                    <span className="truncate">{item.title}</span>
                    {item.sequential && <span className="shrink-0 rounded bg-violet-100 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wide text-violet-700">Sequencial</span>}
                  </p>
                  <p className="text-xs text-slate-500">
                    {item.area?.name && <span className="font-semibold">{item.area.name}</span>}
                    {item.board?.name && <span> · {item.board.name}</span>}
                    {item.createdBy?.nome && <span> · por {item.createdBy.nome}</span>}
                    {item.dueDate && <span> · prazo {formatDate(item.dueDate)}</span>}
                  </p>
                </button>
                <div className="flex w-40 shrink-0 items-center gap-2">
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-200">
                    <div className={`h-full rounded-full transition-all ${item.progress >= 100 ? 'bg-emerald-500' : 'bg-cyan-500'}`} style={{ width: `${item.progress}%` }} />
                  </div>
                  <span className="text-xs font-black text-slate-700">{item.progress}%</span>
                </div>
              </div>

              {/* Estágios na ordem da sequência; mesma etapa = paralelo (empilhados) */}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {(() => {
                  const stageGroups: PipelineStage[][] = [];
                  item.stages.forEach(s => {
                    const last = stageGroups[stageGroups.length - 1];
                    if (last && last[0].sequenceOrder === s.sequenceOrder) last.push(s);
                    else stageGroups.push([s]);
                  });
                  return stageGroups.map((group, gi) => (
                    <div key={gi} className="flex min-w-0 max-w-full items-center gap-2">
                      {gi > 0 && <span className="shrink-0 text-slate-300">→</span>}
                      <div className={`flex min-w-0 max-w-full flex-col gap-1 ${group.length > 1 ? 'rounded-xl border border-dashed border-indigo-200 bg-indigo-50/30 p-1.5' : ''}`}>
                        {group.length > 1 && (
                          <span className="px-1 text-[9px] font-black uppercase tracking-wide text-indigo-500">Em paralelo</span>
                        )}
                        {group.map(s => {
                  const isBottleneck = s.id === item.bottleneckId;
                  return (
                      <button key={s.id} onClick={() => setOpenTaskId(s.id)}
                        title={`${s.title}${s.assignee ? ` — ${s.assignee.nome}` : ''}${s.status !== 'DONE' ? ` — parado há ${s.ageDays}d` : ''}${s.deliveredLate ? ` — ENTREGUE COM ${s.lateDays}d DE ATRASO${s.assignee ? ` por ${s.assignee.nome}` : ''}` : ''}${s.locked ? ' — aguardando etapa anterior' : ''}`}
                        className={`flex min-w-0 max-w-full flex-col items-start gap-0.5 rounded-xl border px-2.5 py-1 text-[11px] font-semibold transition hover:shadow ${
                          isBottleneck
                            ? 'border-red-300 bg-red-50 text-red-700 ring-2 ring-red-200'
                            : s.deliveredLate
                              ? 'border-orange-300 bg-orange-50 text-orange-800'
                              : s.status === 'DONE'
                                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                : s.locked
                                  ? 'border-dashed border-slate-300 bg-slate-50 text-slate-400'
                                  : 'border-slate-200 bg-slate-50 text-slate-600'
                        }`}>
                        <span className="flex w-full min-w-0 flex-wrap items-center gap-1.5">
                          {s.locked && <span className="shrink-0" title="Aguardando a etapa anterior">🔒</span>}
                          {s.area?.name && <span className="max-w-[90px] shrink-0 truncate opacity-70">{s.area.name}:</span>}
                          <span className="min-w-0 max-w-[160px] truncate">{s.title}</span>
                          <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold ${STATUS_COLORS[s.status] || 'bg-slate-200 text-slate-600'}`}>{STATUS_LABELS[s.status] || s.status}</span>
                          <span className="font-black">{s.progress}%</span>
                          {s.status !== 'DONE' && s.ageDays > 0 && <span className={isBottleneck ? 'font-black' : 'opacity-60'}>{s.ageDays}d</span>}
                          {s.overdue && <span title="Atrasada">⚠</span>}
                        </span>
                        {/* Entrega atrasada + prazos (original → alongado pelo atraso anterior) */}
                        {(s.deliveredLate || s.dueDate) && (
                          <span className="flex flex-wrap items-center gap-1.5 text-[10px] font-medium">
                            {s.deliveredLate && (
                              <span className="font-black text-red-600">✗ entregue +{s.lateDays}d atrasado{s.assignee ? ` (${s.assignee.nome.split(' ')[0]})` : ''}</span>
                            )}
                            {s.dueDate && !s.deliveredLate && (
                              <span className="opacity-70">prazo {formatDate(s.dueDate)}</span>
                            )}
                            {s.dueAdjusted && s.status !== 'DONE' && (
                              <span className="font-bold text-amber-600" title={`Prazo alongado em ${s.delayCarryDays} dia(s) pelo atraso das etapas anteriores`}>
                                → {formatDate(s.dueAdjusted)} (+{s.delayCarryDays}d)
                              </span>
                            )}
                          </span>
                        )}
                      </button>
                  );
                })}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          ))}
        </div>
      )}

      {openTaskId && (
        <TaskDetailModal taskId={openTaskId} areas={areas} users={users}
          onClose={() => setOpenTaskId(null)} onChanged={load} />
      )}
    </div>
  );
}
